import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Container, Row, Col, Button, Spinner } from 'react-bootstrap'
import ResizableTable from '../components/ResizableTable'
import PaginationComponent from '../components/Pagination'
import SearchInputWithClear from '../components/SearchInputWithClear'
import CopyableText from '../components/CopyableText'
import { amazonAPI } from '../api/amazonAPI'
import { formatDateTime, formatCurrency } from '../utils/commonUtils'
import { formatErrorResponse } from '../utils/errorHandlers'
import { createColumnToggleDropdown, useTableState } from '../utils/tableUtils'

function AmazonAdPerformanceDetailScreen() {
    const { reportId } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const [returnFilters] = useState(location.state?.returnFilters || '')

    const [report, setReport] = useState(null)
    const [reportLoading, setReportLoading] = useState(true)
    const [error, setError] = useState('')

    const [rowsData, setRowsData] = useState([])
    const [rowsLoading, setRowsLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [searchInputValue, setSearchInputValue] = useState('')
    const debounceTimeoutRef = useRef(null)
    const [sortColumn, setSortColumn] = useState(null)
    const [sortDirection, setSortDirection] = useState(null)
    const [currentPage, setCurrentPage] = useState(1)
    const [totalItems, setTotalItems] = useState(0)
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize())

    const [summary, setSummary] = useState(null)

    const initialTableState = {
        visibility: {
            date: true, asin: true, impressions: true, clicks: true,
            cost: true, adSales: true, adUnitsSold: true, purchases: true, acos: true,
        },
        widths: {}
    }
    const { columnState, updateColumnVisibility } = useTableState('amazon_ad_performance', initialTableState)
    const columnVisibility = columnState.visibility || initialTableState.visibility
    const toggleColumnVisibility = (columnId) => updateColumnVisibility(columnId, !columnVisibility[columnId])
    const columnLabels = {
        date: 'Date', asin: 'ASIN', impressions: 'Impressions', clicks: 'Clicks',
        cost: 'Spend', adSales: 'Ad Sales', adUnitsSold: 'Units Sold',
        purchases: 'Purchases', acos: 'ACoS',
    }

    useEffect(() => {
        const fetchReport = async () => {
            setReportLoading(true)
            try {
                const data = await amazonAPI.adPerformance.get(reportId)
                setReport(data)
            } catch (err) {
                setError(formatErrorResponse(err))
            } finally {
                setReportLoading(false)
            }
        }
        fetchReport()
    }, [reportId])

    const fetchRows = useCallback(async () => {
        if (!report) return
        setRowsLoading(true)
        setError('')
        try {
            const params = {
                page: currentPage,
                pageSize: pageSize,
            }
            if (searchTerm) params.searchTerm = searchTerm
            if (sortColumn) {
                params.sortColumn = sortColumn
                params.sortDirection = sortDirection || 'asc'
            }

            const data = await amazonAPI.adPerformance.getRows(reportId, params)
            if (data && data.results !== undefined) {
                setRowsData(data.results)
                setTotalItems(data.count)
                if (data.summary) {
                    setSummary(data.summary)
                }
            } else {
                setRowsData(data || [])
                setTotalItems((data || []).length)
            }
        } catch (err) {
            setError(formatErrorResponse(err))
        } finally {
            setRowsLoading(false)
        }
    }, [report, reportId, currentPage, pageSize, searchTerm, sortColumn, sortDirection])

    useEffect(() => { fetchRows() }, [fetchRows])

    const handleSearch = (e) => {
        const value = e.target.value
        setSearchInputValue(value)
        if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current)
        debounceTimeoutRef.current = setTimeout(() => { setSearchTerm(value); setCurrentPage(1) }, 300)
    }
    const handleClearSearch = () => { setSearchInputValue(''); setSearchTerm(''); setCurrentPage(1) }

    const handleSort = (column, direction) => { setSortColumn(column); setSortDirection(direction); setCurrentPage(1) }

    const columns = [
        columnVisibility.date && { id: 'date', header: 'Date', sortable: true, cell: (row) => row.date || '' },
        columnVisibility.asin && { id: 'asin', header: 'ASIN', sortable: true, cell: (row) => row.asin ? <CopyableText value={row.asin} toastLabel="ASIN" searchTerm={searchTerm} /> : '' },
        columnVisibility.impressions && { id: 'impressions', header: 'Impressions', sortable: true, cell: (row) => row.impressions != null ? row.impressions.toLocaleString() : '' },
        columnVisibility.clicks && { id: 'clicks', header: 'Clicks', sortable: true, cell: (row) => row.clicks != null ? row.clicks : '' },
        columnVisibility.cost && { id: 'cost', header: 'Spend', sortable: true, cell: (row) => row.cost != null ? formatCurrency(row.cost) : '' },
        columnVisibility.adSales && { id: 'adSales', header: 'Ad Sales', sortable: true, cell: (row) => row.ad_sales != null ? formatCurrency(row.ad_sales) : '' },
        columnVisibility.adUnitsSold && { id: 'adUnitsSold', header: 'Units Sold', sortable: true, cell: (row) => row.ad_units_sold != null ? row.ad_units_sold : '' },
        columnVisibility.purchases && { id: 'purchases', header: 'Purchases', sortable: true, cell: (row) => row.purchases != null ? row.purchases : '' },
        columnVisibility.acos && { id: 'acos', header: 'ACoS', sortable: false, cell: (row) => row.ad_sales > 0 ? ((row.cost / row.ad_sales) * 100).toFixed(1) + '%' : '' },
    ].filter(Boolean)

    if (reportLoading) {
        return (
            <Container fluid className="px-4 py-4">
                <div className="text-center py-4"><Spinner animation="border" /></div>
            </Container>
        )
    }

    const headerSubtitle = report
        ? `${report.seller_account_name || report.account_name || ''} | ${report.start_date || ''} to ${report.end_date || ''}${report.collection_type ? ` (${report.collection_type})` : ''}`
        : ''

    return (
        <Container fluid className="px-4 py-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h1 className="mb-0">Ad Performance</h1>
                    {headerSubtitle && <p className="text-muted mb-0">{headerSubtitle}</p>}
                </div>
                <Button variant="outline-primary" onClick={() => navigate('/amazon-ad-performance', { state: { returnFilters } })}>
                    <i className="fas fa-arrow-left me-2"></i>Back to List
                </Button>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            {rowsLoading && rowsData.length === 0 ? (
                <div className="text-center py-4"><Spinner animation="border" /></div>
            ) : (
                <>
                    {(summary || totalItems > 0) && (
                        <Row className="mb-4 g-3">
                            <Col><div className="bg-primary text-white p-3 rounded text-center"><h5 className="mb-1">Total ASINs</h5><h3 className="mb-0">{summary?.total_asins ?? totalItems}</h3></div></Col>
                            <Col><div className="bg-info text-white p-3 rounded text-center"><h5 className="mb-1">Total Impressions</h5><h3 className="mb-0">{(summary?.total_impressions ?? 0).toLocaleString()}</h3></div></Col>
                            <Col><div className="bg-secondary text-white p-3 rounded text-center"><h5 className="mb-1">Total Clicks</h5><h3 className="mb-0">{(summary?.total_clicks ?? 0).toLocaleString()}</h3></div></Col>
                            <Col><div className="bg-warning text-white p-3 rounded text-center"><h5 className="mb-1">Total Spend</h5><h3 className="mb-0">{formatCurrency(summary?.total_spend ?? 0)}</h3></div></Col>
                            <Col><div className="bg-success text-white p-3 rounded text-center"><h5 className="mb-1">Total Ad Sales</h5><h3 className="mb-0">{formatCurrency(summary?.total_ad_sales ?? 0)}</h3></div></Col>
                        </Row>
                    )}
                    <div className="d-flex align-items-center mb-3">
                        <div>{createColumnToggleDropdown(columnVisibility, columnLabels, toggleColumnVisibility)}</div>
                        <div className="ms-auto" style={{ maxWidth: '300px' }}>
                            <SearchInputWithClear value={searchInputValue} onChange={handleSearch}
                                onClear={handleClearSearch} placeholder="Search ASIN..." />
                        </div>
                    </div>
                    {rowsData.length === 0 ? (
                        <p className="text-muted">No ad performance data found.</p>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <ResizableTable columns={columns} data={rowsData} tableId="amazon_ad_performance"
                                    enableSorting={true} onSort={handleSort}
                                    sortColumn={sortColumn} sortDirection={sortDirection}
                                    initialColumnWidths={{
                                        date: 120, asin: 140, impressions: 120, clicks: 100,
                                        cost: 120, adSales: 120, adUnitsSold: 110, purchases: 110, acos: 100,
                                    }}
                                    striped hover className="table-sm" />
                            </div>
                            <div className="mt-3">
                                <PaginationComponent
                                    currentPage={currentPage}
                                    totalItems={totalItems}
                                    onPageChange={setCurrentPage}
                                    onItemsPerPageChange={setPageSize}
                                />
                            </div>
                        </>
                    )}
                </>
            )}
        </Container>
    )
}

export default AmazonAdPerformanceDetailScreen
